import React, { useRef, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMap, GeoJSON } from 'react-leaflet'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'
import { makeStyles } from '@material-ui/core/styles'
import { useMetadataVisibility } from '../../context/MetadataVisibilityContext'
import { useOptionalAuth } from '../../context/AuthContext'
import { TRACES_COLORS, tracesStatus, tracesTooltipHtml } from '../../utils/traces'

// FAO GAUL admin units saved into parcel.metadata by the backend (company 400).
// Shown as dedicated translated tooltip lines (app + embedded) for company 400.
const GAUL_TOOLTIP_FIELDS = [
  { key: 'gaul_country', label: 'Country' },
  { key: 'gaul_region', label: 'Region' },
  { key: 'gaul_province', label: 'Province' },
]

const useStyles = makeStyles((theme) => ({
  alert: {
    '& .MuiAlert-root': {
      margin: 0,
    },
    '& .MuiAlert-icon': {
      height: theme.spacing(0.5),
    },
    '& .MuiAlert-message': {
      fontSize: theme.spacing(2),
    },
  },
}))

// Configuration for deforestation risk levels
const RISK_CONFIG = {
  high: {
    label: 'HIGH RISK',
    color: '#FF0000',
    style: { fillColor: '#FF0000', color: '#FF0000' }
  },
  more_info_needed: {
    label: 'MORE INFO NEEDED',
    color: '#1EBBFF',
    style: { fillColor: '#1EBBFF', color: '#1EBBFF' }
  },
  low: {
    label: 'LOW RISK',
    color: '#0066FF',
    style: { fillColor: '#0066FF', color: '#0066FF' }
  }
}

const OVERLAP_STYLE = { color: 'orange' }
const DEFAULT_PARCEL_TIP_COLOR = '#FA0101'
const DRAW_MODE_STYLE = { fill: false, fillOpacity: 0 }

const Parcels = ({
  parcels,
  layersState,
  handleOpenDialog,
  width,
  parcelColor,
  drawMode,
  embedded,
  selectedResolution,
  detectOverlapping,
  hasDeforestationAuditData,
  skipInitialFitBounds = false,
}) => {
  const classes = useStyles()
  const groupRef = useRef()
  const map = useMap()
  const { t, i18n } = useTranslation()
  const { visibleKeys } = useMetadataVisibility()
  const { loggedUser } = useOptionalAuth()
  const isCompany400 = loggedUser?.company?.id === 400

  // Memoize processed data to avoid unnecessary recalculations
  const processedData = useMemo(() => ({
    ...parcels,
    features: parcels.features.map(feature => ({
      ...feature,
      geometry: feature.geometry,
    })),
  }), [parcels])

  // Helper function to add styled text to tooltip
  const addTooltipText = useCallback((tooltip, text, color) => {
    return tooltip + `<br /><b style="color:${color}; fontSize: 17px">${text}</b>`
  }, [])

  // Handle deforestation tooltip and styling
  const handleDeforestationData = useCallback((feature, layer, tooltip) => {
    const isDeforestationMode = selectedResolution?.resolution === 'DEFORESTATION' || embedded

    if (!isDeforestationMode) {
      return addTooltipText(tooltip, t('ParcelTip'), DEFAULT_PARCEL_TIP_COLOR)
    }

    // Handle deforestation audit data (v2)
    if (hasDeforestationAuditData) {
      const riskValue = feature.properties.deforestation_v2

      if (riskValue && RISK_CONFIG[riskValue]) {
        const config = RISK_CONFIG[riskValue]
        layer.setStyle(config.style)
        return addTooltipText(tooltip, config.label, config.color)
      }
    }

    return addTooltipText(tooltip, t('ParcelTip'), DEFAULT_PARCEL_TIP_COLOR)
  }, [selectedResolution, embedded, hasDeforestationAuditData, t, addTooltipText])

  // Handle overlap styling
  const handleOverlapStyling = useCallback((feature, layer) => {
    const computedAttributes = feature.properties.computed_attributes

    if (computedAttributes?.has_overlap || computedAttributes?.overlaps_protected_areas || computedAttributes?.overlaps_indigenous_areas) {
      layer.setStyle(OVERLAP_STYLE)
    }
  }, [])

  // Create base tooltip content
  const createBaseTooltip = useCallback((feature) => {
    let tooltip = `<b>${t('Name')}</b>: ${feature.properties.name} <br />
            <b>${t('ID')}</b>: ${feature.id} <br />
            <b>${t('Hectares')}</b>: ${feature.properties.hectares} ha`
    const metadata = feature.properties.metadata
    // GAUL admin units (country/region/province): company 400, app + embedded.
    // Bold key, normal value.
    if (metadata && isCompany400) {
      GAUL_TOOLTIP_FIELDS.forEach(({ key, label }) => {
        if (metadata[key]) {
          tooltip += `<br /><b>${t(label)}</b>: ${metadata[key]}`
        }
      })
    }
    if (metadata && visibleKeys.length > 0) {
      visibleKeys.forEach((key) => {
        // gaul_* keys already shown above with translated labels for company 400
        if (isCompany400 && key.startsWith('gaul_')) return
        if (metadata[key] !== undefined) {
          tooltip += `<br /><b>${key}</b>: ${metadata[key]}`
        }
      })
    }
    return tooltip
  }, [t, visibleKeys, isCompany400])

  // Handle click events
  const handleFeatureClick = useCallback((feature) => {
    handleOpenDialog({
      img_url: null,
      layer: 'Parcels',
      parcel: parcels.features.find(f => f.id === feature.id) ?? feature,
    })
  }, [handleOpenDialog, parcels.features])

  // Main onEachFeature function
  const onEachFeature = useCallback((feature, layer) => {
    if (!drawMode) {
      let tooltip = createBaseTooltip(feature)

      // Handle deforestation data
      tooltip = handleDeforestationData(feature, layer, tooltip)

      // Handle overlap styling
      handleOverlapStyling(feature, layer)

      const computed = feature.properties.computed_attributes
      const tracesState = tracesStatus(computed)
      if (tracesState) {
        // Preserve the deforestation fill; compatibility is a separate dashed outline,
        // coloured by severity so it stays distinct from the solid orange overlap outline.
        layer.setStyle({ color: TRACES_COLORS[tracesState], dashArray: '6 4', weight: 2 })
        tooltip += tracesTooltipHtml(computed, t)
      }

      // Bind tooltip
      layer.bindTooltip(tooltip)
    }

    // Add click handler
    layer.on({
      click: () => handleFeatureClick(feature),
    })
  }, [
    drawMode,
    createBaseTooltip,
    handleDeforestationData,
    handleOverlapStyling,
    t,
    handleFeatureClick
  ])

  // Memoize padding configuration
  const paddingConfig = useMemo(() =>
    isWidthUp('sm', width)
      ? { padding: [-20, 80], paddingTopLeft: [130, 80] }
      : {}
  , [width])

  // Initial map fitting
  useEffect(() => {
    if (!drawMode && groupRef.current && !skipInitialFitBounds) {
      map.fitBounds(groupRef.current.getBounds())
    }
  }, [drawMode, map, skipInitialFitBounds])

  // Handle data updates
  useEffect(() => {
    if (groupRef.current && !drawMode) {
      groupRef.current.clearLayers()
      groupRef.current.addData(processedData)
      if (!skipInitialFitBounds) {
        map.fitBounds(groupRef.current.getBounds(), paddingConfig)
      }
    }
  }, [processedData, drawMode, map, paddingConfig, skipInitialFitBounds])

  // Handle layer visibility
  useEffect(() => {
    const parcelLayer = layersState.find(layer => layer.name === 'Parcels')
    const isVisible = parcelLayer?.visible

    if (!groupRef.current) return

    if (!isVisible) {
      map.removeLayer(groupRef.current)
    } else {
      map.addLayer(groupRef.current)
    }
  }, [layersState, map])

  // Handle drawMode layer positioning
  useEffect(() => {
    if (groupRef.current && drawMode) {
      groupRef.current.bringToBack()
    }
  }, [drawMode])

  // Memoize the key for GeoJSON to prevent unnecessary re-renders. Tooltips are built once in
  // onEachFeature when the layer is created, so the language is part of the key: switching it
  // rebuilds the layer and its tooltips (TRACES warning included) in the new language.
  const geoJsonKey = useMemo(() =>
    `deforestation-${selectedResolution?.resolution === 'DEFORESTATION'}-${hasDeforestationAuditData ? 'has-audit' : 'no-audit'}-${i18n.language}-${JSON.stringify(processedData)}-${JSON.stringify(visibleKeys)}`
  , [selectedResolution, hasDeforestationAuditData, i18n.language, processedData, visibleKeys])

  return (
    <GeoJSON
      key={geoJsonKey}
      data={processedData}
      color={parcelColor}
      name="Parcels"
      onEachFeature={onEachFeature}
      ref={groupRef}
      style={drawMode ? DRAW_MODE_STYLE : undefined}
    />
  )
}

export default withWidth()(Parcels)
